import { useMemo, useState } from "react";

import { type Floor } from "@eisox/api/core-api";
import { Plan, Select } from "@eisox/design-system";
import { ShiftIcon } from "@eisox/icons";
import { ParaglideMessage } from "@inlang/paraglide-js-react";
import { useQuery } from "@tanstack/react-query";

import ExpandedRoom from "~/components/expended-room";
import { FieldContainer } from "~/components/field-container";
import { floorPlanQueries } from "~/features/floor/floor-plan.queries";
import { m } from "~/paraglide/messages";
import type { EnhancedRoom } from "~/types/room.type";
import { toggleIdsInSelection } from "~/utils/plan-selection";

import { normalizeRoomIds, type EditableAirHandlerUnitRoomAssociation } from "../helpers/association.helper";

const NAME = "RoomAssociationPlan";

interface RoomAssociationPlanProps {
  floors: Floor[];
  rooms: EnhancedRoom[];
  association: EditableAirHandlerUnitRoomAssociation;
  onRoomIdsChange: (roomIds: string[]) => void;
}

const RoomAssociationPlan: React.FC<RoomAssociationPlanProps> = ({ floors, rooms, association, onRoomIdsChange }) => {
  const [selectedFloorId, setSelectedFloorId] = useState(floors[0]?.id);
  const floor = floors.find(item => item.id === selectedFloorId);

  const selectedRoomIds = association.roomIds;

  const { data: imgUrl } = useQuery({
    ...floorPlanQueries.byId(floor?.id ?? ""),
    enabled: floor !== undefined,
    staleTime: 0,
  });

  const roomsOnFloor = useMemo(() => rooms.filter(room => room.floor.id === floor?.id), [floor?.id, rooms]);

  const handleToggleRoom = (roomId: string) => {
    onRoomIdsChange(
      normalizeRoomIds(
        selectedRoomIds.includes(roomId) ? selectedRoomIds.filter(id => id !== roomId) : [...selectedRoomIds, roomId],
      ),
    );
  };

  const handleRectangleSelect = (itemIds: string[]) => {
    const roomIdsOnFloor = roomsOnFloor.map(room => room.id);
    const selectedFromFloor = itemIds.filter(id => roomIdsOnFloor.includes(id));
    const otherFloorSelections = selectedRoomIds.filter(id => !roomIdsOnFloor.includes(id));
    const currentFloorSelections = selectedRoomIds.filter(id => roomIdsOnFloor.includes(id));
    const toggled = toggleIdsInSelection(currentFloorSelections, selectedFromFloor);
    onRoomIdsChange(normalizeRoomIds([...otherFloorSelections, ...toggled]));
  };

  if (!floor) return null;

  return (
    <div className="gap-3 min-h-0 min-w-0 flex flex-1 flex-col">
      <div className="gap-3 flex items-center justify-between max-[1200px]:flex-col max-[1200px]:items-start">
        <FieldContainer
          classNames={{ container: "w-80 min-w-80 max-[1200px]:min-w-0 max-[1200px]:w-full" }}
          label={m.level()}
        >
          <Select.Root multiple={false} value={selectedFloorId} onValueChange={setSelectedFloorId}>
            <Select.Trigger className="w-80 max-[1200px]:w-full">
              <Select.Value placeholder={m.select_level()} />
            </Select.Trigger>
            <Select.Content>
              <Select.Viewport>
                {floors.map(floor => (
                  <Select.Item key={floor.id} value={floor.id}>
                    {floor.name}
                  </Select.Item>
                ))}
              </Select.Viewport>
            </Select.Content>
          </Select.Root>
        </FieldContainer>
        <div className="gap-1 flex flex-1 flex-col items-end max-[1200px]:items-start">
          <p className="text-sm text-primary-dark-gray">
            <ParaglideMessage
              message={m.air_handler_unit_room_association_plan_instructions}
              markup={{
                i: () => (
                  <span className="gap-1 rounded-sm px-1.5 py-0.5 inline-flex items-center bg-primary-gray">
                    <ShiftIcon className="size-3" />
                    MAJ
                  </span>
                ),
              }}
            />
          </p>
        </div>
      </div>
      <Plan.Root rectangleSelection onRectangleSelect={handleRectangleSelect}>
        <Plan.Wrapper className="min-h-80 w-full! flex-1!" plan={imgUrl}>
          {roomsOnFloor.map(room => {
            const selected = selectedRoomIds.includes(room.id);
            return (
              <Plan.Item
                key={room.id}
                selectionId={room.id}
                position={{ x: room.floor.x, y: room.floor.y }}
                origin="bottom center"
              >
                <ExpandedRoom selected={selected} onClick={() => handleToggleRoom(room.id)}>
                  {room.name}
                </ExpandedRoom>
              </Plan.Item>
            );
          })}
        </Plan.Wrapper>
      </Plan.Root>
    </div>
  );
};

RoomAssociationPlan.displayName = NAME;

export { RoomAssociationPlan };
export type { RoomAssociationPlanProps };
